import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../helpers/constants";
import {
  BulkUploadStatusPayload,
  BulkUploadStatusResponse,
} from "../../../pages/bulkCreateProgram/modelsAndSchema/Models";
import apiInstance from "../../instances/apiInstance";
import {
  BulkUploadSubmitParams,
  CancelProcessParams,
  DownloadReportParams,
  DownloadSmartTemplateParams,
  DownloadSmartTemplatePostParams,
} from "./models";

export const useGetDownloadSmartTemplateByClientId = () => {
  return useMutation({
    mutationFn: async (params: DownloadSmartTemplateParams) => {
      const { clientId = "" } = params;
      const res = await apiInstance.get(
        API_ENDPOINTS.BULK_CREATE_PROGRAM_DOWNLOAD_SMART_TEMPLATE(clientId),
        {
          responseType: "blob",
          headers: {
            "Content-Type": "application/octet-stream",
          },
        },
      );
      let contentTypeHeader = res.headers["content-type"];
      /**converting binary file to blob here */
      let blob = new Blob([res.data], {
        type: contentTypeHeader?.toString().toLowerCase(),
      });
      return blob;
    },
  });
};

export const useBulkUploadSubmit = () => {
  return useMutation({
    mutationFn: async ({ params }: { params: BulkUploadSubmitParams }) => {
      const response = await apiInstance.post(
        API_ENDPOINTS.BULK_CREATE_PROGRAM_UPLOAD_SUBMIT,
        params,
      );
      return response;
    },
    retry: false,
  });
};

export const useGetDownloadFeedbackReport = () => {
  return useMutation({
    mutationFn: async (params: DownloadReportParams) => {
      const { bulkImportId = "", reportType = "" } = params;
      const res = await apiInstance.get(
        API_ENDPOINTS.BULK_CREATE_PROGRAM_DOWNLOAD_REPORT(bulkImportId),
        {
          params: { reportType }, // Add reportType as query param
          responseType: "blob",
          headers: {
            "Content-Type": "application/octet-stream",
          },
        },
      );
      let contentTypeHeader = res.headers["content-type"];
      /**converting binary file to blob here */
      let blob = new Blob([res.data], {
        type: contentTypeHeader?.toString().toLowerCase(),
      });
      return blob;
    },
  });
};
export const useGetBulkUploadStatusById = () => {
  const [params, setParams] = useState<BulkUploadStatusPayload>();

  const query = useQuery({
    queryKey: ["useGetBulkUploadStatusById", params],
    queryFn: async (): Promise<BulkUploadStatusResponse> => {
      if (!params) return {} as BulkUploadStatusResponse;
      const res = await apiInstance.get(
        API_ENDPOINTS.getBulkUploadStatusById(
          params.bulkImportId,
          params.category,
        ),
      );
      return res.data;
    },
    enabled: !!params,
    retry: false,
  });

  const refetchWithParams = (newParams: BulkUploadStatusPayload) => {
    setParams(newParams);
    query.refetch();
  };

  return { ...query, refetchWithParams };
};

export const useCancelBulkProcess = () => {
  return useMutation({
    mutationFn: async ({ params }: { params: CancelProcessParams }) => {
      const { bulkImportId = "", action = "" } = params;

      const response = await apiInstance.post(
        API_ENDPOINTS.getCancelBulkProcess(bulkImportId),
        { action },
      );
      return response;
    },
    retry: false,
  });
};

// POST method for bulk edit program download smart template
export const useDownloadSmartTemplate = () => {
  return useMutation({
    mutationFn: async (params: DownloadSmartTemplatePostParams) => {
      const res = await apiInstance.post(
        API_ENDPOINTS.BULK_EDIT_PROGRAM_DOWNLOAD_SMART_TEMPLATE,
        params, // body
        {
          responseType: "blob",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      const contentTypeHeader = res.headers["content-type"];
      const blob = new Blob([res.data], {
        type: contentTypeHeader?.toString().toLowerCase(),
      });

      return blob;
    },
  });
};
